/**
 * Exceptional convoy tenders — 05/02/2026 (Q1 retour user prod).
 *
 * « Les contrats Convoi exceptionnel passent forcément par Appel d'Offres »
 *
 * Tenders rares (1-2 disponibles à la fois, refresh très lent), filtrés
 * par éligibilité stricte :
 *   • Permis convoi exceptionnel actif (`inv-permit-exceptional`)
 *   • Au moins 1 tracteur `mantor-heavyhaul-xxl` en flotte (idle ou non)
 *   • Phase >= 4 (le joueur doit être bien développé)
 *
 * Quand le joueur gagne un de ces AO, le contrat porte
 * `kind: 'convoy_exceptional'`. À l'étape de staffing, `acceptContract`
 * route vers `startConvoyExceptionnel` au lieu du flow contrat standard.
 * La résolution est gérée par `tickConvoyExceptionnel` (aléas, presse).
 */

import { MISSION_ARCHETYPES_FOR_TENDERS } from './exceptionalArchetypes';

export const EXCEPTIONAL_TENDERS = [
  {
    id: 'exc-tender-eolienne-bretagne',
    clientName: 'NordVent Énergie',
    sector: 'aerospatial',
    tier: 'national',
    kind: 'convoy_exceptional',
    archetypeId: 'wind-turbine',
    requiresFleet: 1,
    requiresRseTier: 'none',
    requiresMedicalPct: 80,
    baseDailyRevenue: 13500,
    durationDays: 7,
    distance: 420,
    cargo: 'Pale d\'éolienne offshore',
    interimCostRange: [3500, 5500],
    competitors: ['Convois Spéciaux Pro', 'Heavyhaul Europe'],
    competitorPriceRange: [82000, 108000],
    narrative: {
      announce:
        "NordVent Énergie a un besoin urgent : convoyer une pale d'éolienne " +
        "offshore de 65 m de Brest à un parc inland sur 420 km avec arrêts nuit " +
        "obligatoires. Mission prestigieuse, opérationnellement exigeante.",
      win:
        "NordVent vous confie l'opération. Préparez tracteur Heavyhaul + " +
        "chauffeur certifié + escorte gendarmerie. La mission démarre dès " +
        "staffing validé.",
      loss:
        "NordVent a choisi un concurrent plus expérimenté en convois éoliens.",
    },
  },
  {
    id: 'exc-tender-fusee-ariane',
    clientName: 'ArianeSpace Logistics',
    sector: 'aerospatial',
    tier: 'national',
    kind: 'convoy_exceptional',
    archetypeId: 'rocket-stage',
    requiresFleet: 1,
    requiresRseTier: 'bronze',
    requiresMedicalPct: 100,
    baseDailyRevenue: 20000,
    durationDays: 6,
    distance: 380,
    cargo: 'Étage de fusée Ariane',
    interimCostRange: [4000, 5500],
    competitors: ['Convois Spéciaux Pro'],
    competitorPriceRange: [108000, 132000],
    narrative: {
      announce:
        "ArianeSpace Logistics cherche un transporteur certifié pour " +
        "convoyer un étage central de lanceur Ariane 6 sur 380 km. Escorte " +
        "gendarmerie obligatoire, route fermée la nuit. Référence absolue " +
        "dans le métier en cas de succès.",
      win:
        "ArianeSpace vous confie la mission de prestige. Briefing sécurité " +
        "le lendemain du staffing.",
      loss:
        "ArianeSpace a privilégié un concurrent disposant déjà de références " +
        "spatiales.",
    },
  },
  {
    id: 'exc-tender-maison-prefab',
    clientName: 'Maisons Préfabri Pro',
    sector: 'industrie',
    tier: 'regional',
    kind: 'convoy_exceptional',
    archetypeId: 'prefab-house',
    requiresFleet: 1,
    requiresRseTier: 'none',
    requiresMedicalPct: 60,
    baseDailyRevenue: 14500,
    durationDays: 5,
    distance: 280,
    cargo: 'Maison modulaire 12×4 m',
    interimCostRange: [3000, 4500],
    competitors: ['TransNord Express'],
    competitorPriceRange: [62000, 82000],
    narrative: {
      announce:
        "Maisons Préfabri Pro a besoin de convoyer un module habitation " +
        "préfabriqué 12 × 4 m (18 t) de leur usine vers un client final. " +
        "Levage grue à l'arrivée, route à itinéraire dédié.",
      win:
        "Maisons Préfabri vous confie le convoi. Date de livraison fixée à " +
        "réception du tracteur sur site.",
      loss:
        "Maisons Préfabri a opté pour un transporteur local avec un meilleur prix.",
    },
  },
  {
    id: 'exc-tender-presse-industrielle',
    clientName: 'SteelForge Industries',
    sector: 'industrie',
    tier: 'national',
    kind: 'convoy_exceptional',
    archetypeId: 'industrial-press',
    requiresFleet: 1,
    requiresRseTier: 'bronze',
    requiresMedicalPct: 80,
    baseDailyRevenue: 12500,
    durationDays: 8,
    distance: 510,
    cargo: 'Presse hydraulique 45T',
    interimCostRange: [3800, 5200],
    competitors: ['Heavyhaul Europe'],
    competitorPriceRange: [88000, 115000],
    narrative: {
      announce:
        "SteelForge Industries doit déplacer une presse forge de 45 tonnes " +
        "sur 510 km. Fragilité aux vibrations : vitesse plafonnée à 60 km/h. " +
        "Mission sensible mais juteuse.",
      win:
        "SteelForge valide votre dossier. Préparez la mobilisation.",
      loss:
        "SteelForge n'a pas retenu votre offre. Trop chère ou pas assez argumentée.",
    },
  },
];

/**
 * Filtre d'éligibilité pour les tenders convois exceptionnels.
 * Le tender n'apparaît dans le board AO que si TOUTES ces conditions
 * sont remplies.
 */
export function isPlayerEligibleForExceptionalTender(state) {
  const HEAVYHAUL_VEHICLE_ID = 'mantor-heavyhaul-xxl';
  const PERMIT_INVESTMENT_ID = 'inv-permit-exceptional';
  const hasPermit = !!(state.activeInvestments && state.activeInvestments[PERMIT_INVESTMENT_ID]);
  const hasHeavyhaul = (state.vehicles || []).some(v => v.modelId === HEAVYHAUL_VEHICLE_ID);
  const phase = state.phase || 1;
  return hasPermit && hasHeavyhaul && phase >= 4;
}

// Re-export pour les helpers/tests.
export { MISSION_ARCHETYPES_FOR_TENDERS };
